import {
    Box,
    Flex,
    FlexProps,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Tag,
    TagLabel,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import IconButton from '../IconButton/IconButton';

interface TagsProps extends FlexProps {
    items: any[];
    dataKey?: string;
    tooltipKey?: string;
    globalProps?: any;
    onClick: (item: any) => void;
    onDelete?: (item: any) => void;
    selected: any;
    uniqueId?: string;
    tagHeight?: string;
    isDisabled?: boolean;
}

function Tags({
    items,
    dataKey = '',
    tooltipKey = '',
    onClick,
    onDelete,
    globalProps,
    selected,
    tagHeight = '25px',
    uniqueId = 'id',
    isDisabled = false,
    ...others
}: TagsProps) {
    const currentColors = useCurrentColors();
    const { 1: setHovered } = useState<number | null>(null);
    return (
        <Flex {...others}>
            {items?.map((item, index) => {
                const id = typeof item === 'string' ? item : item?.[uniqueId];
                const error = typeof item === 'string' ? '' : item?.error;
                return (
                    <Tag
                        key={id || index}
                        margin="5px"
                        height={tagHeight}
                        minHeight={tagHeight}
                        padding="3px"
                        rounded="full"
                        variant="solid"
                        cursor={!isDisabled ? 'pointer' : 'normal'}
                        backgroundColor={
                            id === selected?.[uniqueId]
                                ? (currentColors.selectedTag || currentColors.primaryColor)
                                : 'transparent'
                        }
                        border={
                            error
                                ? `1px solid ${currentColors.errorText}`
                                : `1px solid ${currentColors.brand['700']}`
                        }
                        onClick={() => onClick(item)}
                        onMouseEnter={() => setHovered(index)}
                        onMouseLeave={() => setHovered(null)}>
                        {onDelete ? (
                            <Flex>
                                <IconButton
                                    background="transparent"
                                    cursor="pointer"
                                    border="none"
                                    color={
                                        id === selected?.[uniqueId]
                                            ? currentColors.tooltipTextColor
                                            : '#6E6E6E'
                                    }
                                    width="20px"
                                    height="20px"
                                    aria-label="remove"
                                    icon={'ActClose'}
                                    backgroundColor="transparent"
                                    _hover={{ backgroundColor: 'transparent' }}
                                    _focus={{
                                        border: '0px',
                                        backgroundColor: 'transparent',
                                        outline: 'none',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(item);
                                    }}
                                />
                            </Flex>
                        ) : null}
                        {tooltipKey &&
                        typeof item !== 'string' &&
                        Array.isArray(item?.[tooltipKey]) ? (
                            <Popover
                                trigger="hover"
                                placement="bottom">
                                <PopoverTrigger>
                                    <TagLabel
                                        color={
                                            id === selected?.[uniqueId]
                                                ? currentColors.tooltipTextColor
                                                : '#6E6E6E'
                                        }
                                        fontWeight="bold"
                                        padding="3px"
                                        fontSize="13px"
                                        pl="4px">
                                        {dataKey && typeof item !== 'string'
                                            ? item[dataKey]
                                            : item}
                                    </TagLabel>
                                </PopoverTrigger>
                                <Portal>
                                    <PopoverContent
                                        width="max-content"
                                        zIndex="9999">
                                        <PopoverArrow />
                                        <PopoverBody
                                            bg={currentColors?.tooltipBg}
                                            color={
                                                currentColors?.tooltipTextColor
                                            }
                                            fontSize="11px"
                                            p="4px"
                                            zIndex="9999"
                                            lineHeight="11px"
                                            _focus={{
                                                border: 'none',
                                                outline: 'none',
                                            }}
                                            _active={{
                                                border: 'none',
                                                outline: 'none',
                                            }}>
                                            {tooltipKey &&
                                            typeof item !== 'string' ? (
                                                Array.isArray(
                                                    item[tooltipKey]
                                                ) ? (
                                                    <VStack
                                                        spacing="5px"
                                                        padding="5px"
                                                        alignItems="flex-start">
                                                        {item[tooltipKey]?.map(
                                                            (
                                                                element: string
                                                            ) => {
                                                                return (
                                                                    <Box
                                                                        key={
                                                                            element
                                                                        }>
                                                                        {
                                                                            element
                                                                        }
                                                                    </Box>
                                                                );
                                                            }
                                                        )}
                                                    </VStack>
                                                ) : (
                                                    item[tooltipKey]
                                                )
                                            ) : (
                                                item
                                            )}
                                        </PopoverBody>
                                    </PopoverContent>
                                </Portal>
                            </Popover>
                        ) : (
                            <Tooltip
                                aria-label={
                                    tooltipKey && typeof item !== 'string'
                                        ? item[tooltipKey]
                                        : item
                                }
                                label={
                                    tooltipKey && typeof item !== 'string'
                                        ? item[tooltipKey]
                                        : item
                                }
                                zIndex={9999}
                                hasArrow>
                                <TagLabel
                                    color={
                                        id === selected?.[uniqueId]
                                            ? currentColors.tooltipTextColor
                                            : '#6E6E6E'
                                    }
                                    fontWeight="bold"
                                    padding="3px"
                                    fontSize="13px"
                                    pl="4px">
                                    {dataKey && typeof item !== 'string'
                                        ? item[dataKey]
                                        : item}
                                </TagLabel>
                            </Tooltip>
                        )}
                    </Tag>
                );
            })}
        </Flex>
    );
}

export default Tags;
